import React, { useState, useEffect } from 'react';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { useNavigate, useLocation } from 'react-router-dom';
import { useUser } from '../context/UserContext';
import ReservationSummary from '../ ReservationSummary';
import ConfirmModal from '../components/ConfirmationModal';

import './confirmationPersonalData.css';
import BackModal from '../components/BackModal';
import ProgressBar from '../components/ProgressBar';

const apiUrl = process.env.REACT_APP_API_URL;
const apiKey = process.env.REACT_APP_API_KEY!;

const handleKeyPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
  const charCode = event.which ? event.which : event.keyCode;
  if (charCode < 48 || charCode > 57) {
    event.preventDefault();
  }
};

interface FormValues {
  name: string;
  lastName: string;
  motherLastName: string;
  phone: string;
  secondaryPhone: string;
  email: string;
  birthdate?: string;
  healthInsurance: string;
}

interface BodyData {
  codHour: string,
  patientCod?: string,
  patientPhonePrimary: string | undefined,
  patientPhoneSecondary: string | undefined,
  patientEmail: string | undefined,
  medicalPlan: string | undefined,
  patientNames?: string,
  patientFatherLastname?: string,
  patientMotherLastname?: string,
  patientBirthdate?: string,
  docType?: string,
  docNumber?: string
}

interface HealthInsuranceList {
  cod: string;
  name: string;
}

const ConfirmationPersonalData: React.FC = () => {
  const { user, setUser, setDate, date } = useUser();
  const navigate = useNavigate();
  const location = useLocation();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isBackModalOpen, setIsBackModalOpen] = useState(false);
  const [isUserRegister, setIsUserRegister] = useState(false);
  const [formValues, setFormValues] = useState<FormValues | null>(null);
  const [isCorfirmationFailded, setIsCorfirmationFailded] = useState(false);
  const [messageCode, setMessageCode] = useState("default");
  const [healthInsuranceList, setHealtInsuranceList] = useState<HealthInsuranceList[]>([])

  const validatedPhone = (value: string) => {
    if (!isUserRegister) {
      const phoneRegex = /^\d{9}$/;
      return phoneRegex.test(value);
    } else {
      const phoneAndAsteriskRegex = /^[0-9*]{9}$/;
      return phoneAndAsteriskRegex.test(value);
    }
  };

  const validatedFixedPhone = (value: string | undefined): boolean => {
    if (!isUserRegister && value) {
      const phoneRegex = /^\d{9}$/;
      return phoneRegex.test(value);
    } else if (isUserRegister && value) {
      const phoneWithAsterisksRegex = /^[0-9*]{9}$/;;
      return phoneWithAsterisksRegex.test(value);
    } else {
      return true
    }
  };

  const validatedEmail = (value: string) => {
    if (!isUserRegister) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      return emailRegex.test(value);
    } else {
      const emailAndAsteriskRegex = /^(?=.*@).+$/;

      return emailAndAsteriskRegex.test(value);
    }
  };


  const formatDocId = (docId: string, docType: string) => {
    if (docType === "rut") {
      let rutNumber = docId.replace(/\./g, '').replace(/-/g, '');
      const rutStr = rutNumber.toString();
      const body = rutStr.slice(0, -1);
      const verifier = rutStr.slice(-1);
      const formattedRUT = `${body}-${verifier}`;
      return formattedRUT;
    } else {
      return docId
    }
  }

  const fetchScheduleHour = async () => {
    const url = `${apiUrl}/api/schedule-hour`;
    let bodyData: BodyData = {
      codHour: date.hourId,
      patientPhonePrimary: formValues?.phone,
      patientPhoneSecondary: formValues?.secondaryPhone,
      patientEmail: formValues?.email,
      medicalPlan: formValues?.healthInsurance,
      patientNames: formValues?.name,
      patientFatherLastname: formValues?.lastName,
      patientMotherLastname: formValues?.motherLastName,
      patientBirthdate: formValues?.birthdate,
      docType: user.docType,
      docNumber: formatDocId(user.docId, user.docType)
    };

    if (user.patientCod !== "") {
      bodyData = {
        patientCod: user.patientCod,
        codHour: date.hourId,
        patientPhonePrimary: formValues?.phone,
        patientPhoneSecondary: formValues?.secondaryPhone,
        patientEmail: formValues?.email,
        medicalPlan: formValues?.healthInsurance
      };
    }

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-API-KEY': apiKey
        },
        body: JSON.stringify(bodyData)
      });

      const data = await response.json();
      if (!response.ok) {
        setMessageCode(data.data.code);
        setIsCorfirmationFailded(true);
        throw new Error(`Error: ${response.statusText}`);
        
      }
      setIsModalOpen(true);
      navigate('/summary');
    } catch (error) {
      console.error('Error al enviar la solicitud:', error);
    }
  };

  const SignupSchema = Yup.object().shape({
    name: Yup.string().required('El nombre es obligatorio'),
    lastName: Yup.string().required('El apellido paterno es obligatorio'),
    motherLastName: Yup.string(),
    phone: Yup.string()
      .required('El número es obligatorio')
      .test('is-valid-number', 'Número de celular no válido. Debe tener 9 dígitos', validatedPhone),
    secondaryPhone: Yup.string()
      .test('is-valid-number', 'Número de teléfono no válido.', validatedFixedPhone),
    email: Yup.string()
      .required('El correo es obligatorio')
      .test('is-valid-mail', 'Correo no válido.', validatedEmail),
    birthdate: Yup.date()
      .required('La fecha de cumpleaños es obligatoria')
      .max(new Date(), 'La fecha no puede ser en el futuro'),
    healthInsurance: Yup.string()
      .required('La previsión de salud es obligatoria')
  });

  const initialValues: FormValues = {
    name: user.name,
    lastName: user.lastName,
    motherLastName: user.motherLastName,
    phone: user.phone,
    secondaryPhone: user.secondaryPhone,
    email: user.email,
    birthdate: user.birthdate || '',
    healthInsurance:  user.medicalplan
  };

  useEffect(() => {
    if (user.name !== '') {
      setIsUserRegister(true)
    } else {
      setIsUserRegister(false)
    }
  }, [user.name])

  useEffect(() => {
    const fetchMedicalPlans = async () => {
      const bodyData = {
        'medicalPlanType': user.healthInsurance
      }
      try {
        const response = await fetch(
          `${apiUrl}/api/get-medical-plans`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-API-KEY': apiKey
          },
          body: JSON.stringify(bodyData)
        })
        const data = await response.json();
        setHealtInsuranceList(data.data);
      } catch (error) {
        console.error('Error al obtener los nombres:', error);
      }
    };

    fetchMedicalPlans();
  }, []);

  const handleConfirm = () => {
    if (formValues) {
      fetchScheduleHour();
      setUser({
        ...user,
        ...formValues,
      });
    }
  };
  const fetchFreeHour = async () => {
    const url = `${apiUrl}/api/free-hour`;
    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-API-KEY': apiKey
        },
        body: JSON.stringify({ codHour: date.hourId })
      });

      if (!response.ok) {
        throw new Error(`Error: ${response.statusText}`);
      }

      const data = await response.json();
    } catch (error) {
      console.error('Error al enviar la solicitud:', error);
    }
  };

  useEffect(() => {
    const {hourId, docId} = date;
    if (location.pathname === '/confirmation' && (hourId === "" || docId === "")){      
      navigate('/date');
    }
  }, [])

  const handleBack = () => {
    navigate('/date');
    fetchFreeHour();
    setDate({
      ...date,
      isPreRegister: true,
      hourId: '',
      hora: ''
    });
  };

  return (
    <>
      <ProgressBar loadPercentage={75} />
      <div className='confirmation-data-form'>
        <ReservationSummary />
        {!isUserRegister ? <h3>Ingresar información del paciente</h3> : <h3>Actualizar información en caso de ser necesario</h3>}
        <p className='confirmation-data-id'>
          <span>{user.docType === "passport" ? "pasaporte" : user.docType}</span>
          <span>{formatDocId(user.docId, user.docType)}</span>
        </p>
        <Formik
          initialValues={initialValues}
          validationSchema={SignupSchema}
          onSubmit={(values) => {
            setFormValues(values);
            setIsModalOpen(true);
          }}
        >
          {({ errors, touched }) => (
            <Form>
              <div className='confirmation-data-item'>
                <label htmlFor="Nombre">Nombre</label>
                <Field name="name" type="text" disabled={isUserRegister} />
                <ErrorMessage className='error-message' name="name" component="div" />
              </div>
              <div className='confirmation-data-items'>
                <div className='confirmation-data-item'>
                  <label htmlFor="apellidoPaterno">Apellido Paterno</label>
                  <Field name="lastName" type="text" disabled={isUserRegister} />
                  <ErrorMessage className='error-message' name="lastName" component="div" />
                </div>
                <div className='confirmation-data-item'>
                  <label htmlFor="apellidoMaterno">Apellido Materno</label>
                  <Field name="motherLastName" type="text" disabled={isUserRegister} />
                </div>
              </div>
              {!isUserRegister &&
                <div className='confirmation-data-item'>
                  <label htmlFor='birthdate'>Fecha de Nacimiento</label>
                  <Field name='birthdate' type='date' />
                  <ErrorMessage className='error-message' name='birthdate' component='div' />
                </div>
              }
              <div className='confirmation-data-items'>
                <div className='confirmation-data-phone-item'>
                  <label htmlFor="phone">Celular de contacto</label>
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <span>+56</span>
                    <Field name="phone" type="text" maxLength="9" pattern="[0-9*]*" onKeyPress={handleKeyPress} />
                  </div>
                  <ErrorMessage className='error-message' name="phone" component="div" />
                </div>

                <div className='confirmation-data-phone-item'>
                  <label htmlFor="secondaryPhone">Teléfono de contacto</label>
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <span>+56</span>
                    <Field name="secondaryPhone" type="text" maxLength="9" pattern="[0-9*]*" onKeyPress={handleKeyPress} />
                  </div>
                  <ErrorMessage className='error-message' name="secondaryPhone" component="div" />
                </div>
              </div>
              <div className='confirmation-data-item'>
                <label htmlFor="email">Correo electrónico</label>
                <Field name="email" type="text" />
                <ErrorMessage className='error-message' name="email" component="div" />
              </div>
              <div className='personal-data-item'>
                <label htmlFor='healthInsurance'>Previsión de salud</label>
                <Field as='select' name='healthInsurance'>
                  <option value=''>Seleccione previsión</option>
                  {healthInsuranceList.length > 0 && (
                    healthInsuranceList.map((insurance) => (
                      <option key={insurance.cod} value={insurance.cod}>
                        {insurance.name}
                      </option>
                    ))
                  )}
                </Field>
                <ErrorMessage className='error-message' name='healthInsurance' component='div' />
              </div>

              <p>La información será validada al momento de recepcionar su cita en el Centro Médico</p>

              <div className='confirmation-data-button'>
                <button type="button" onClick={() => setIsBackModalOpen(true)}>Volver</button>
                <button type="submit">Confirmar</button>
              </div>
            </Form>
          )}
        </Formik>
        {isModalOpen &&
          <ConfirmModal
            onRequestClose={() => {setIsCorfirmationFailded(false); setIsModalOpen(false)}}
            isCorfirmationFailded={isCorfirmationFailded}
            onConfirm={handleConfirm}
            formValues={formValues}
            messageCode={messageCode}
          />
        }
        <BackModal
          isOpen={isBackModalOpen}
          onRequestClose={() => setIsBackModalOpen(false)}
          onConfirm={handleBack} />
      </div>
    </>
  );
}

export default ConfirmationPersonalData;
